import React, { useState, useEffect, useRef } from 'react';
import { IndianRupee, ArrowLeft, Calendar, Wallet, X, Eye, EyeOff } from 'lucide-react';
import { supabase, SUPABASE_ENABLED } from './lib/supabaseClient';

const TODAY_TZ = 'Asia/Kolkata';

interface CollectionRecord {
  date: string;
  time: string;
  mode: string;
  amount: string;
}

interface Customer {
  id: number;
  name: string;
  number: string | null;
  history: CollectionRecord[];
  emi_amount?: number;
}

interface LedgerCollection {
  id: number;
  account_id: number;
  date: string;
  amount: number;
  mode: 'Cash' | 'UPI' | 'PP';
  created_at: string;
  remarks?: string | null;
}

interface LedgerSummaryItem {
  id: number;
  account_name: string;
  phone: string | null;
  account_no: string | null;
  principal_amount: number;
  emi_amount: number;
  total_collected: number;
  repayment_done: number;
  outstanding_due: number;
}

interface CollectionScheduleRow {
  id: number;
  account_id: number;
  installment_no: number;
  due_date: string;
  due_amount: number;
  paid_amount: number;
  balance_amount: number;
  status: 'pending' | 'partial' | 'paid';
  paid_date: string | null;
  last_payment_id: number | null;
}

const collectionsData: Customer[] = [
  { id: 1, name: "FEBIN 1", number: "8075247562", history: [] },
  { id: 2, name: "FEBIN 2", number: null, history: [] },
  { id: 3, name: "FEBIN 3", number: null, history: [] },
  { id: 4, name: "FEBIN 4", number: null, history: [] },
  { id: 5, name: "SAHID", number: null, history: [] },
  { id: 6, name: "JAVED", number: "7902455547", history: [] },
  { id: 7, name: "ASIF", number: "6325699390", history: [] },
  { id: 8, name: "MANOHAR", number: "9959493023", history: [] },
  { id: 9, name: "JAI SANKAR", number: null, history: [] },
  { id: 10, name: "MANOHAR - SIVA", number: null, history: [] },
  { id: 11, name: "MANOHAR RAJENDRA", number: null, history: [] },
  { id: 12, name: "VENKATESH - WATER", number: null, history: [] },
  { id: 13, name: "YS MANI", number: "8977961210", history: [] },
  { id: 14, name: "POLIAN", number: "9398990801", history: [] },
  { id: 15, name: "BRAMHIAH AC", number: "9502008211", history: [] },
  { id: 16, name: "SURYA NARAYANA", number: "8686688777", history: [] },
  { id: 17, name: "KIRAN", number: "9908842557", history: [] },
  { id: 18, name: "HUSSAIN", number: "8897441180", history: [] },
  { id: 19, name: "ABDULLA", number: "8073805648", history: [] },
  { id: 20, name: "RAVI MUNI", number: null, history: [] },
  { id: 21, name: "RAVI - JAVED", number: null, history: [] },
  { id: 22, name: "RAVI", number: null, history: [] },
  { id: 23, name: "RAVI - YS MANI", number: null, history: [] },
  { id: 24, name: "FEBIN - YS MANI", number: null, history: [] },
  { id: 25, name: "MURALI", number: null, history: [] },
  { id: 26, name: "IZAZ", number: null, history: [] },
  { id: 27, name: "RIYAZ", number: null, history: [] },
  { id: 28, name: "SONU", number: null, history: [] },
  { id: 29, name: "Hussain - Manikanta", number: null, history: [] },
  { id: 30, name: "Manohar Guru", number: null, history: [] }
];

const initialCollections: Customer[] = SUPABASE_ENABLED ? [] : collectionsData;

function App() {
  const [collections, setCollections] = useState<Customer[]>(initialCollections);
  // Removed legacy input/mode states in EMI-based workflow
  // PP Modal state (Option 1: unlimited PP with remarks)
  const [showPpModal, setShowPpModal] = useState<boolean>(false);
  const [ppModalAccountId, setPpModalAccountId] = useState<number | null>(null);
  const [ppAmount, setPpAmount] = useState<string>('');
  const [ppRemarks, setPpRemarks] = useState<string>('');
  const [ppError, setPpError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<'list' | 'history' | 'ledger' | 'create-loan'>('list');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState<string | null>(null);
  const [personLedger, setPersonLedger] = useState<LedgerSummaryItem[]>([]);
  const [personLedgerRows, setPersonLedgerRows] = useState<{
    account_id: number;
    account_name: string;
    date: string;
    amount: number;
    mode: string;
    created_at: string;
  }[]>([]);
  const [centralAccounts, setCentralAccounts] = useState<LedgerSummaryItem[]>([]);
  const [centralAccountMeta, setCentralAccountMeta] = useState<Record<number, {
    status?: string;
    loan_date?: string;
    term_days?: number;
    amount_held?: number;
  }>>({});
  const [todayTotal, setTodayTotal] = useState(0);
  const [usingSupabase, setUsingSupabase] = useState<boolean>(SUPABASE_ENABLED);
  const [loadingCollections, setLoadingCollections] = useState<boolean>(false);
  const [showTotal, setShowTotal] = useState<boolean>(false);
  const [showPinModal, setShowPinModal] = useState<boolean>(false);
  const [pinValue, setPinValue] = useState<string>('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [todaysCollections, setTodaysCollections] = useState<LedgerCollection[]>([]);

  // Per-account ledger navigation/state
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  type LedgerAccount = {
    id: number;
    account_name: string;
    emi_amount?: number | null;
    principal_amount?: number | null;
    amount_held?: number | null;
    outstanding_due?: number | null;
    status?: string | null;
    loan_date?: string | null;
  } | null;
  const [ledgerAccount, setLedgerAccount] = useState<LedgerAccount>(null);
  const [ledgerRows, setLedgerRows] = useState<LedgerCollection[]>([]);
  const [ledgerSummary, setLedgerSummary] = useState<{ totalCollected: number; pendingDays: number; lastPaymentDate: string | null }>({ totalCollected: 0, pendingDays: 0, lastPaymentDate: null });
  const [outstandingAmount, setOutstandingAmount] = useState<number>(0);
  const [scheduleRows, setScheduleRows] = useState<CollectionScheduleRow[]>([]);

  const [createLoanForm, setCreateLoanForm] = useState({
    frequency: 'DAILY',
    account_name: '',
    phone: '',
    account_no: '',
    loan_date: getISTCollectionDate(),
    emi_start_date: getISTCollectionDate(),
    principal_amount: '',
    term_days: '',
    emi_amount: '',
    hold_percent: '',
    amount_held: '',
    status: 'ACTIVE'
  });
  const [creatingLoan, setCreatingLoan] = useState<boolean>(false);
  const [createLoanError, setCreateLoanError] = useState<string | null>(null);
  const [createLoanSuccess, setCreateLoanSuccess] = useState<string | null>(null);

  const handleUnlockTotal = () => {
    if (pinValue === '9700') {
      setShowTotal(true);
      setShowPinModal(false);
      setPinError(null);
      setPinValue('');
    } else {
      setPinError('Incorrect PIN');
    }
  };

  const handleMaskTotal = () => {
    setShowTotal(false);
  };

  const today = new Intl.DateTimeFormat('en-US', {
    timeZone: TODAY_TZ,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date());


  const formatISTTime = (dateLike: Date | string) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: TODAY_TZ,
      hour: '2-digit',
      minute: '2-digit'
    }).format(typeof dateLike === 'string' ? new Date(dateLike) : dateLike);

  // IST date display for arbitrary YYYY-MM-DD values
  const formatISTDateDisplay = (dateIso: string) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: TODAY_TZ,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(dateIso));

  function getISTCollectionDate(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: TODAY_TZ });
  }

  function getISTCollectionDateDisplay(): string {
    const now = new Date();
    const istStr = now.toLocaleString('en-US', { timeZone: TODAY_TZ });
    const ist = new Date(istStr);
    const hours = ist.getHours();
    const minutes = ist.getMinutes();
    if (hours === 0 && minutes < 45) {
      ist.setDate(ist.getDate() - 1);
    }
    return new Intl.DateTimeFormat('en-IN', { timeZone: TODAY_TZ }).format(ist);
  }

  // Helper: has any full payment (Cash/UPI) today for account
  const hasFullPaymentToday = (accountId: number) =>
    todaysCollections.some(row => row.account_id === accountId && (row.mode === 'Cash' || row.mode === 'UPI'));

  // Load accounts and today's collections from Supabase if configured
  useEffect(() => {
    const loadFromSupabase = async () => {
      setLoadingCollections(true);
      if (!SUPABASE_ENABLED) {
        setUsingSupabase(false);
        setLoadingCollections(false);
        return;
      }

      const { data: accounts, error: accErr } = await supabase
        .from('ledger_accounts_new')
        .select('id, account_name, emi_amount, status')
        .order('id');

      if (accErr || !accounts || accounts.length === 0) {
        console.error('Failed to load accounts:', accErr);
        setUsingSupabase(false);
        setLoadingCollections(false);
        return;
      }

      // Fetch today's collections (ALL rows) via helper
      await loadTodaysCollectionsFromSupabase();

      const mapped: Customer[] = (accounts as any[]).map((a: any) => {
        const history: CollectionRecord[] = [];
        return {
          id: Number(a?.id ?? 0),
          name: String(a?.account_name ?? ''),
          number: null,
          history,
          emi_amount: Number(a?.emi_amount ?? 0)
        };
      });

      setCollections(mapped);
      setUsingSupabase(true);
      setLoadingCollections(false);

      // After loading collections, refresh today's total from Supabase using IST date
      await loadTodayTotalSupabase();
    };

    loadFromSupabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all rows for today's IST date from Supabase and store as array
  const loadTodaysCollectionsFromSupabase = async () => {
    if (!SUPABASE_ENABLED) return;
    const date = getISTCollectionDate();
    const { data, error } = await supabase
      .from('ledger_collections')
      .select('id, account_id, date, amount, mode, created_at')
      .eq('date', date);
    if (error) {
      console.error('Failed to load today collections:', error);
      setTodaysCollections([]);
      return;
    }
    setTodaysCollections((data || []) as unknown as LedgerCollection[]);
  };

  // Supabase: load today's total by IST date (12:45 AM cutoff)
  const loadTodayTotalSupabase = async () => {
    if (!SUPABASE_ENABLED) return;
    const date = getISTCollectionDate();
    const { data, error } = await supabase
      .from('ledger_collections')
      .select('amount')
      .eq('date', date);
    if (error) {
      console.error('Failed to load today total:', error);
      return;
    }
    let sum = 0;
    (data || []).forEach((row: any) => { sum += Number(row.amount) || 0; });
    setTodayTotal(sum);
  };

  // Removed local recompute of today total; rely on Supabase-only loader

  // Removed legacy amount/mode handlers in favor of EMI-based buttons

  // (removed) handleViewHistory — no longer used in the single-row list UI

  // (removed) normalizePersonName — no longer required for central ledger navigation

  // Load all accounts for a person from ledger_summary view
  // (removed) handleViewLedger — replaced by handleViewCentralLedgerAll for central view

  // Central Ledger: load all rows across all people
  const handleViewCentralLedgerAll = async () => {
    setSelectedPersonName('All');
    setSelectedAccountId(null);
    if (usingSupabase) {
      const { data: accounts, error: accErr } = await supabase
        .from('ledger_accounts_new')
        .select('id, account_name, principal_amount, emi_amount, amount_held, loan_date, term_days, status')
        .order('id');

      const { data: allCollections } = await supabase
        .from('ledger_collections')
        .select('id, account_id, date, amount, mode, created_at');

      if (!accErr && accounts) {
        const metaMap: Record<number, { status?: string; loan_date?: string; term_days?: number; amount_held?: number }> = {};
        (accounts as any[]).forEach((a: any) => {
          metaMap[Number(a.id)] = {
            status: a?.status,
            loan_date: a?.loan_date,
            term_days: a?.term_days,
            amount_held: a?.amount_held != null ? Number(a.amount_held) : undefined,
          };
        });
        setCentralAccountMeta(metaMap);

        const summaries: LedgerSummaryItem[] = (accounts as any[]).map((a: any) => {
          const rows = ((allCollections as any[]) || []).filter((r: any) => Number(r.account_id) === Number(a.id));
          const totalCollected = rows.reduce((s: number, r: any) => s + (Number(r?.amount) || 0), 0);
          const repaymentDone = Number(a?.emi_amount) ? totalCollected / Number(a.emi_amount) : 0;
          const outstandingDue = (Number(a?.principal_amount) || 0) - totalCollected;
          return {
            id: Number(a.id),
            account_name: String(a.account_name ?? ''),
            phone: null,
            account_no: null,
            principal_amount: Number(a.principal_amount ?? 0),
            emi_amount: Number(a.emi_amount ?? 0),
            total_collected: totalCollected,
            repayment_done: repaymentDone,
            outstanding_due: outstandingDue,
          } as LedgerSummaryItem;
        });
        setCentralAccounts(summaries);
      } else {
        setCentralAccounts([]);
        setCentralAccountMeta({});
      }

      const { data: rows, error: rowsErr } = await supabase
        .from('ledger_collections')
        .select('account_id, date, amount, mode, created_at')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (!rowsErr && rows) {
        const nameById = new Map<number, string>((accounts as any[] || []).map((a: any) => [Number(a.id), String(a.account_name ?? '')]));
        const mapped = (rows as any[]).map((r: any) => ({
          account_id: r.account_id,
          account_name: nameById.get(Number(r.account_id)) || String(r.account_id),
          date: r.date,
          amount: r.amount,
          mode: r.mode,
          created_at: r.created_at,
        }));
        setPersonLedgerRows(mapped);
      } else {
        console.error('Failed to load central ledger rows:', rowsErr);
        setPersonLedgerRows([]);
      }
    } else {
      setPersonLedgerRows([]);
    }
    setPersonLedger([]);
    setCurrentPage('ledger');
  };

  const handleBack = () => {
    setCurrentPage('list');
    setSelectedCustomer(null);
    setSelectedAccountId(null);
  };

  // Removed unused handleUpdateClick to satisfy TypeScript build


  // Removed getTodayCollection; local lock uses Cash/UPI-only check

  const getTotalCollection = (customer: Customer) => {
    return customer.history.reduce((sum, h) => sum + parseFloat(h.amount || '0'), 0);
  };

  // Removed currency formatter from UI to fully hide EMI amounts

  // Option 1: Common insert logic
  const recordCollection = async (
    accountId: number,
    mode: 'Cash' | 'UPI' | 'PP',
    amount: number,
    remarks?: string
  ) => {
    const todayStr = getISTCollectionDateDisplay();
    const collectionDateIso = getISTCollectionDate();
    const timestamp = formatISTTime(new Date());

    // Enforce single full EMI (Cash/UPI) per day
    if (usingSupabase && (mode === 'Cash' || mode === 'UPI')) {
      const { data: existingRows, error: preErr } = await supabase
        .from('ledger_collections')
        .select('id, amount, mode, created_at')
        .eq('account_id', accountId)
        .eq('date', collectionDateIso)
        .in('mode', ['Cash', 'UPI'])
        .limit(1);
      if (!preErr && existingRows && existingRows.length > 0) {
        // Already collected today via Cash/UPI — reflect in local history for context and bail
        const row = existingRows[0] as { amount?: number; mode?: string; created_at?: string };
        const timeStr = row?.created_at
          ? new Date(row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
          : timestamp;
        setCollections(prev => prev.map(c =>
          c.id === accountId
            ? {
                ...c,
                history: c.history.some(h => h.date === todayStr)
                  ? c.history
                  : [...c.history, { date: todayStr, time: timeStr, mode: String(row?.mode || 'Collected'), amount: String(row?.amount ?? amount ?? 0) }]
              }
            : c
        ));
        return;
      }
    }

    // Insert row in Supabase via RPC (Waterfall Logic)
    if (SUPABASE_ENABLED) {
      const { error } = await supabase.rpc('record_payment_waterfall', {
        p_account_id: Number(accountId),
        p_amount: Number(amount),
        p_date: collectionDateIso,
        p_mode: mode,
        p_remarks: remarks || ''
      });

      if (error) {
        console.error('Payment RPC failed:', error);
      } else {
        // Append to todaysCollections for immediate UI lock and visibility
        setTodaysCollections(prev => [
          ...prev,
          {
            id: -1,
            account_id: accountId,
            date: collectionDateIso,
            amount: Number(amount),
            mode,
            created_at: new Date().toISOString()
          } as LedgerCollection
        ]);
        await loadTodayTotalSupabase();
      }
    }

    // Always reflect in local customer history for context
    setCollections(prev => prev.map(c =>
      c.id === accountId
        ? {
            ...c,
            history: [...c.history, { date: todayStr, time: timestamp, mode, amount: String(amount) }]
          }
        : c
    ));
  };

  const handleCashCollect = async (customerId: number, emiAmount: number = 0) => {
    if (hasFullPaymentToday(customerId)) return; // ignore clicks when already collected
    await recordCollection(customerId, 'Cash', emiAmount || 0);
  };

  const handleUpiCollect = async (customerId: number, emiAmount: number = 0) => {
    if (hasFullPaymentToday(customerId)) return; // ignore clicks when already collected
    await recordCollection(customerId, 'UPI', emiAmount || 0);
  };

  const openPpModal = (customerId: number) => {
    setPpModalAccountId(customerId);
    setPpAmount('');
    setPpRemarks('');
    setPpError(null);
    setShowPpModal(true);
  };

  const submitPpModal = async () => {
    const id = ppModalAccountId;
    if (!id) return;
    const val = Number(ppAmount || '0');
    if (!val || isNaN(val) || val <= 0) {
      setPpError('Enter a valid amount');
      return;
    }
    await recordCollection(id, 'PP', val, ppRemarks || undefined);
    setShowPpModal(false);
    setPpModalAccountId(null);
    setPpAmount('');
    setPpRemarks('');
    setPpError(null);
  };

  // Load ledger for a single account
  const loadAccountLedger = async (accountId: number) => {
    if (!SUPABASE_ENABLED) return;
    try {
      const { data: account, error: accErr } = await supabase
        .from('ledger_accounts_new')
        .select('id, account_name, emi_amount, principal_amount, amount_held, status, outstanding_due, loan_date, term_days, no_of_days_rx')
        .eq('id', accountId)
        .single();
      if (accErr) {
        console.error('Failed to load account:', accErr);
        setLedgerAccount(null);
      } else {
        setLedgerAccount(account as any);
        const outstandingFromAccount = (account as any).outstanding_due;
        if (typeof outstandingFromAccount === 'number') {
          setOutstandingAmount(outstandingFromAccount || 0);
        } else {
          setOutstandingAmount(0);
        }
      }

      const { data: rows, error: rowsErr } = await supabase
        .from('ledger_collections')
        .select('id, account_id, date, amount, mode, created_at, remarks')
        .eq('account_id', accountId)
        .order('date', { ascending: true })
        .order('created_at', { ascending: true });
      if (rowsErr) {
        console.error('Failed to load ledger rows:', rowsErr);
        setLedgerRows([]);
        setLedgerSummary({ totalCollected: 0, pendingDays: 0, lastPaymentDate: null });
      } else {
        const list = (rows || []) as unknown as LedgerCollection[];
        setLedgerRows(list);
        const totalCollected = list.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const lastPaymentDate = list.length > 0 ? list[list.length - 1].date : null;
        const todayIso = getISTCollectionDate();
        const { count: pendingDaysCount } = await supabase
          .from('collection_schedule')
          .select('*', { count: 'exact', head: true })
          .eq('account_id', accountId)
          .neq('status', 'paid')
          .lte('due_date', todayIso);

        setLedgerSummary({ totalCollected, pendingDays: pendingDaysCount || 0, lastPaymentDate });

        const { data: scheduleData, error: scheduleErr } = await supabase
          .from('collection_schedule')
          .select('id, account_id, installment_no, due_date, due_amount, paid_amount, balance_amount, status, paid_date, last_payment_id')
          .eq('account_id', accountId)
          .order('due_date', { ascending: true })
          .order('installment_no', { ascending: true });

        if (scheduleErr) {
          console.error('Failed to load schedule:', scheduleErr);
          setScheduleRows([]);
        } else {
          setScheduleRows((scheduleData || []) as CollectionScheduleRow[]);
        }
      }
    } catch (e) {
      console.error('loadAccountLedger error:', e);
    }
  };

  const openAccountLedger = async (accountId: number) => {
    setSelectedPersonName(null);
    setSelectedAccountId(accountId);
    setCurrentPage('ledger');
  };

  useEffect(() => {
    if (currentPage === 'ledger' && selectedAccountId) {
      loadAccountLedger(selectedAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedAccountId]);

  // (removed) getLastFiveCollections — recent history panel removed in new design

  // List Page
  if (currentPage === 'list') {
    return (
      <div className="min-h-screen bg-black text-[#E6E6E6] font-sans w-full overflow-x-hidden">
        {/* Header Row: Date and Today's Total in one line */}
        <div className="bg-black border-b border-[#222222] px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[26px] font-bold text-[#E6E6E6] px-2 md:px-4">
              {today}
            </div>
            <div className="flex items-center gap-3 text-[#E6E6E6] px-2 md:px-4">
              <span className="text-lg md:text-xl font-semibold">
                Today’s Total: {showTotal ? `₹${todayTotal.toFixed(2)}` : '••••'}
              </span>
              {!showTotal ? (
                <button
                  aria-label="Show total"
                  className="text-[#E6E6E6]"
                  onClick={() => { setPinError(null); setPinValue(''); setShowPinModal(true); }}
                >
                  <Eye size={24} />
                </button>
              ) : (
                <button
                  aria-label="Hide total"
                  className="text-[#E6E6E6]"
                  onClick={handleMaskTotal}
                >
                  <EyeOff size={24} />
                </button>
              )}
              <button
                className="text-white/90 text-sm px-3 py-2 border border-white/40 rounded-[8px] hover:text-white hover:border-white/60"
                onClick={() => setCurrentPage('create-loan')}
              >
                Create Loan
              </button>
            </div>
          </div>
        </div>

        {/* Collection List - Tablet-friendly single row layout (monochrome) */}
        <div className="p-4 md:p-6 w-full overflow-x-hidden">
          {loadingCollections ? (
            <div className="text-white p-6 text-3xl">Loading...</div>
          ) : collections.map((customer) => {
            const hasCashOrUpiTodayLocal = customer.history.some(
              h => h.date === getISTCollectionDateDisplay() && (h.mode === 'Cash' || h.mode === 'UPI')
            );
            const hasFullPaymentToday = (accountId: number) =>
              todaysCollections.some(row => row.account_id === accountId && (row.mode === 'Cash' || row.mode === 'UPI'));
            const isLocked = usingSupabase ? hasFullPaymentToday(customer.id) : hasCashOrUpiTodayLocal;

            return (
              <div
                key={customer.id}
                className="flex flex-row items-center justify-between gap-4 py-4 px-4 border-b border-[#222222] bg-black w-full overflow-x-hidden flex-wrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {/* Name column (25%) */}
                <div className="shrink-0 basis-[25%] min-w-[25%] text-left">
                  <button
                    onClick={() => openAccountLedger(customer.id)}
                    className="text-[#E6E6E6] font-bold text-[26px] tracking-[0.5px] leading-none truncate"
                    title={customer.name}
                  >
                    {customer.name}
                  </button>
                </div>
                {/* Buttons (75%) */}
                <div className="flex flex-row gap-3 flex-shrink-0 items-center justify-end text-right">
                  <>
                      {/* Cash EMI */}
                      <button
                        onClick={() => handleCashCollect(customer.id, customer.emi_amount || 0)}
                        disabled={isLocked}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 ${
                          isLocked
                            ? 'bg-[#333333] text-[#AAAAAA] border-[#333333] cursor-not-allowed'
                            : 'bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150'
                        } h-[46px] min-w-[90px] text-[18px] font-semibold`}
                        title={isLocked ? 'Collected' : 'Cash'}
                      >
                        {isLocked ? 'Collected' : 'Cash'}
                      </button>

                      {/* UPI EMI */}
                      <button
                        onClick={() => handleUpiCollect(customer.id, customer.emi_amount || 0)}
                        disabled={isLocked}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 ${
                          isLocked
                            ? 'bg-[#333333] text-[#AAAAAA] border-[#333333] cursor-not-allowed'
                            : 'bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150'
                        } h-[46px] min-w-[90px] text-[18px] font-semibold`}
                        title={isLocked ? 'Collected' : 'UPI'}
                      >
                        {isLocked ? 'Collected' : 'UPI'}
                      </button>

                      {/* PP (modal: amount + remarks) */}
                      <button
                        onClick={() => openPpModal(customer.id)}
                        className={`flex items-center justify-center text-center px-4 py-2 rounded-xl border-2 bg-[#000000] text-[#E6E6E6] border-[#888888] hover:bg-[#DDDDDD] hover:text-[#111111] transition-all duration-150 h-[46px] min-w-[60px] text-[18px] font-semibold`}
                        title={'PP'}
                      >
                        {'PP'}
                      </button>
                  </>
                </div>
              </div>
            );
          })}
        </div>


        {/* PIN Popup */}
        {showPinModal && <PinModal />}

        {/* PP Modal */}
        {showPpModal && <PpModal />}
      </div>
    );
  }

  if (currentPage === 'create-loan') {
    const f = createLoanForm as any;
    const onChange = (k: string, v: string) => setCreateLoanForm(prev => ({ ...prev, [k]: v }));
    const submitCreateLoan = async () => {
      if (!SUPABASE_ENABLED) return;
      setCreatingLoan(true);
      setCreateLoanError(null);
      setCreateLoanSuccess(null);
      const principal = Number(f.principal_amount || 0);
      const term = Number(f.term_days || 0);
      const emi = Number(f.emi_amount || 0);
      const holdPct = Number(f.hold_percent || 0);
      const held = Number(f.amount_held || 0);
      const loanDateStr = String(f.loan_date || '');
      const emiStartStr = String(f.emi_start_date || '');
      const loanDate = new Date(loanDateStr);
      const emiStartDate = new Date(emiStartStr);
      if (emiStartDate.getTime() < loanDate.getTime()) {
        setCreateLoanError('EMI Start Date cannot be before Loan Date');
        setCreatingLoan(false);
        return;
      }
      const masterPayload = {
        frequency: String(f.frequency || 'DAILY'),
        account_name: String(f.account_name || ''),
        phone: f.phone ? String(f.phone) : null,
        account_no: f.account_no ? String(f.account_no) : null,
        loan_date: loanDateStr,
        emi_start_date: emiStartStr,
        principal_amount: principal,
        term_days: term,
        emi_amount: emi,
        hold_percent: holdPct,
        amount_held: held,
        status: 'ACTIVE'
      } as any;
      const { data: masterRow, error: mErr } = await supabase
        .from('ledger_accounts_master')
        .insert(masterPayload)
        .select('id')
        .single();
      if (mErr || !masterRow) {
        setCreateLoanError('Failed to create master account');
        setCreatingLoan(false);
        return;
      }
      const newId = Number((masterRow as any).id);
      const newPayload = {
        id: newId,
        frequency: masterPayload.frequency,
        account_name: masterPayload.account_name,
        phone: masterPayload.phone,
        account_no: masterPayload.account_no,
        loan_date: masterPayload.loan_date,
        emi_start_date: masterPayload.emi_start_date,
        principal_amount: masterPayload.principal_amount,
        term_days: masterPayload.term_days,
        emi_amount: masterPayload.emi_amount,
        hold_percent: masterPayload.hold_percent,
        amount_held: masterPayload.amount_held,
        status: masterPayload.status,
        no_of_days_rx: 0
      } as any;
      const { error: nErr } = await supabase
        .from('ledger_accounts_new')
        .insert(newPayload);
      if (nErr) {
        setCreateLoanError('Failed to create new account');
        setCreatingLoan(false);
        return;
      }
      const rows: any[] = [];
      for (let day = 1; day <= term; day++) {
        const d = new Date(emiStartStr);
        const offset = String(f.frequency || 'DAILY') === 'WEEKLY' ? (day - 1) * 7 : (day - 1);
        d.setDate(d.getDate() + offset);
        const iso = d.toISOString().slice(0, 10);
        rows.push({
          account_id: newId,
          installment_no: day,
          due_date: iso,
          due_amount: emi,
          paid_amount: 0,
          balance_amount: emi,
          status: 'pending'
        });
      }
      const { error: sErr } = await supabase
        .from('collection_schedule')
        .insert(rows);
      if (sErr) {
        setCreateLoanError('Failed to create schedule');
        setCreatingLoan(false);
        return;
      }
      setCreateLoanSuccess('Loan created successfully');
      setCreatingLoan(false);
      setTimeout(() => {
        setSelectedAccountId(newId);
        setCurrentPage('ledger');
      }, 500);
    };
    return (
      <div className="min-h-screen bg-black text-white font-sans w-full overflow-x-hidden">
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">Create Loan</h1>
        </div>
        <div className="p-4 max-w-2xl mx-auto">
          {createLoanError && (
            <div className="bg-red-600 text-white rounded-xl p-3 mb-4">{createLoanError}</div>
          )}
          {createLoanSuccess && (
            <div className="bg-green-600 text-white rounded-xl p-3 mb-4">{createLoanSuccess}</div>
          )}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-white text-lg font-medium mb-2">Frequency</label>
              <select className="bg-black border border-[#333] rounded-xl p-3 text-white w-full" value={createLoanForm.frequency} onChange={e=>onChange('frequency', e.target.value)}>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
              </select>
            </div>
            <input className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Account Name" value={createLoanForm.account_name} onChange={e=>onChange('account_name', e.target.value)} />
            <input className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Phone" value={createLoanForm.phone} onChange={e=>onChange('phone', e.target.value)} />
            <input className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Account No" value={createLoanForm.account_no} onChange={e=>onChange('account_no', e.target.value)} />
            <input type="date" className="bg-black border border-[#333] rounded-xl p-3 text-white" value={createLoanForm.loan_date} onChange={e=>onChange('loan_date', e.target.value)} />
            <input type="date" min={createLoanForm.loan_date} className="bg-black border border-[#333] rounded-xl p-3 text-white" value={createLoanForm.emi_start_date} onChange={e=>onChange('emi_start_date', e.target.value)} />
            <input type="number" className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Principal Amount" value={createLoanForm.principal_amount} onChange={e=>onChange('principal_amount', e.target.value)} />
            <input type="number" className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Term Days" value={createLoanForm.term_days} onChange={e=>onChange('term_days', e.target.value)} />
            <input type="number" className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="EMI Amount" value={createLoanForm.emi_amount} onChange={e=>onChange('emi_amount', e.target.value)} />
            <input type="number" className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Hold Percent" value={createLoanForm.hold_percent} onChange={e=>onChange('hold_percent', e.target.value)} />
            <input type="number" className="bg-black border border-[#333] rounded-xl p-3 text-white" placeholder="Amount Held" value={createLoanForm.amount_held} onChange={e=>onChange('amount_held', e.target.value)} />
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={handleBack} className="px-4 py-3 border border-[#333] rounded-xl text-white/80">Cancel</button>
            <button onClick={submitCreateLoan} disabled={creatingLoan} className="px-4 py-3 border border-white/40 rounded-xl text-white bg-white/10 hover:bg-white/20 disabled:opacity-50">
              {creatingLoan ? 'Saving...' : 'Save Loan'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // History Page
  if (currentPage === 'history' && selectedCustomer) {
    const totalAmount = getTotalCollection(selectedCustomer);
    
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">{selectedCustomer.name}</h1>
          {selectedCustomer.number && (
            <p className="text-white/70 text-base mt-1">{selectedCustomer.number}</p>
          )}
        </div>

        {/* Summary Card */}
        <div className="p-4">
          <div className="bg-black border border-[#333] rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Wallet size={24} className="text-white" />
              <span className="text-base font-bold">Total Collection</span>
            </div>
            <p className="text-3xl font-bold">₹{totalAmount.toFixed(2)}</p>
            <p className="text-white/70 text-base mt-2">{selectedCustomer.history.length} transactions</p>
          </div>
        </div>

        {/* Payment History */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
            <Calendar size={24} className="text-white/70" />
            Payment History
          </h2>
          
          {selectedCustomer.history.length === 0 ? (
            <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
              <p className="text-white/70 text-base">No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedCustomer.history.slice().reverse().map((record, index) => (
                <div
                  key={index}
                  className="bg-black border border-[#333] rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-full border border-[#333]">
                        <IndianRupee size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-base text-white">₹{record.amount}</p>
                        <p className="text-white/70 text-base">{record.mode}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-medium text-white">{record.date}</p>
                      <p className="text-white/70 text-base">{record.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // PIN Popup Modal
  function PinModal() {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (showPinModal) {
        inputRef.current?.focus();
      }
    }, [showPinModal]);

    useEffect(() => {
      // Preserve focus even when error state changes
      if (showPinModal) {
        inputRef.current?.focus();
      }
    }, [pinError]);

    const handleChangePin: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const next = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
      setPinValue(next);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        handleUnlockTotal();
      }
    };

      const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
        // Keep the modal open and preserve focus while typing
        if (showPinModal) {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-black border border-[#333333] rounded-xl p-5 w-[320px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-[#E6E6E6]">Enter PIN</h3>
            <button onClick={() => setShowPinModal(false)} className="text-[#E6E6E6]/70 hover:text-[#E6E6E6]">
              <X size={20} />
            </button>
          </div>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="Enter PIN"
            autoFocus
            value={pinValue}
            onChange={handleChangePin}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[22px] tracking-widest text-center outline-none border border-[#444444]"
          />
          {pinError && (
            <div className="text-[#FF6B6B] text-sm mb-2">{pinError}</div>
          )}
          <div className="flex items-center justify-end gap-3 mt-2">
            <button
              onClick={() => setShowPinModal(false)}
              className="text-[#E6E6E6] px-3 py-2 border-2 border-[#888888] rounded-[10px] hover:bg-[#DDDDDD] hover:text-[#111111]"
            >
              Cancel
            </button>
            <button
              onClick={handleUnlockTotal}
              className="text-[#111111] bg-[#DDDDDD] px-3 py-2 border-2 border-[#DDDDDD] rounded-[10px] hover:opacity-90"
            >
              Unlock
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PP Modal (amount + remarks)
  function PpModal() {
    const amountRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => { amountRef.current?.focus(); }, []);

    const onEnter: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === 'Enter') submitPpModal();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-black border border-[#333333] rounded-xl p-5 w-[360px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-[#E6E6E6]">Part Payment</h3>
            <button onClick={() => setShowPpModal(false)} className="text-[#E6E6E6]/70 hover:text-[#E6E6E6]">
              <X size={20} />
            </button>
          </div>
          <div className="space-y-3">
            <input
              ref={amountRef}
              type="number"
              inputMode="decimal"
              placeholder="Amount"
              value={ppAmount}
              onChange={(e) => setPpAmount(e.target.value)}
              onKeyDown={onEnter}
              className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[18px] outline-none border border-[#444444]"
            />
            <input
              type="text"
              placeholder="Remarks (optional)"
              value={ppRemarks}
              onChange={(e) => setPpRemarks(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#111111] text-white text-[16px] outline-none border border-[#444444]"
            />
            {ppError && <div className="text-[#FF6B6B] text-sm">{ppError}</div>}
          </div>
          <div className="flex items-center justify-end gap-3 mt-3">
            <button
              onClick={() => setShowPpModal(false)}
              className="text-[#E6E6E6] px-3 py-2 border-2 border-[#888888] rounded-[10px] hover:bg-[#DDDDDD] hover:text-[#111111]"
            >
              Cancel
            </button>
            <button
              onClick={submitPpModal}
              className="text-[#111111] bg-[#DDDDDD] px-3 py-2 border-2 border-[#DDDDDD] rounded-[10px] hover:opacity-90"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ledger Page (single account)
  if (currentPage === 'ledger' && selectedAccountId) {
    const acc = ledgerAccount;
    const name = acc?.account_name || `Account ${selectedAccountId}`;
    const status = acc?.status || 'ACTIVE';
    const emi = acc?.emi_amount || 0;
    const principal = acc?.principal_amount || 0;
    const amtHeld = acc?.amount_held || 0;
    const outstanding = outstandingAmount || 0;
    const totalCollected = ledgerSummary.totalCollected || 0;
    // pendingDays used directly in UI below
    const lastPayStr = ledgerSummary.lastPaymentDate ? formatISTDateDisplay(ledgerSummary.lastPaymentDate) : '-';

    return (
      <div className="min-h-screen bg-black text-[#E6E6E6] font-sans w-full overflow-x-hidden">
        {/* Header */}
        <div className="bg-black border-b border-[#222222] px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="text-[#E6E6E6] text-xl px-3 py-2 border-2 border-[#444] rounded-[10px]"
            >
              ← Back
            </button>
            <div className="flex items-center gap-3">
              <div className="text-[26px] font-bold text-[#E6E6E6] truncate px-2">
                {name}
              </div>
              <div className="px-3 py-2 border-2 border-[#888] rounded-full text-sm">
                {status}
              </div>
            </div>
            <button
              onClick={handleViewCentralLedgerAll}
              className="text-white/90 text-sm px-3 py-2 border border-white/40 rounded-[8px] hover:text-white hover:border-white/60"
            >
              Central Ledger
            </button>
          </div>
        </div>

        {/* Summary Grid */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>EMI:</span>
            <span>₹{emi.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Principal:</span>
            <span>₹{principal.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Amt Held:</span>
            <span>₹{amtHeld.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Outstanding:</span>
            <span>₹{outstanding.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>Total Collected:</span>
            <span>₹{totalCollected.toLocaleString('en-IN')}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl">
            <span>No. of Days Pending:</span>
            <span>{(ledgerSummary.pendingDays || 0)}{(ledgerSummary.pendingDays || 0) === 1 ? ' day' : ' days'}</span>
          </div>
          <div className="bg-black border border-[#333] rounded-xl p-4 flex justify-between text-xl md:col-span-2">
            <span>Last Payment On:</span>
            <span>{lastPayStr}</span>
          </div>
        </div>

        {/* Schedule List */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-[#E6E6E6] mb-3">Schedule</h2>
          <div className="bg-black border border-[#333] rounded-xl overflow-hidden">
            {scheduleRows.length === 0 ? (
              <div className="p-6 text-center text-[#E6E6E6]/70">
                No schedule rows yet.
              </div>
            ) : (
              <div className="divide-y divide-[#222] max-h-[60vh] overflow-y-auto">
                {scheduleRows.map((row) => {
                  const statusColor =
                    row.status === 'paid'
                      ? 'text-green-400'
                      : row.status === 'partial'
                      ? 'text-yellow-400'
                      : 'text-[#AAAAAA]';

                  const paidDateLabel = row.paid_date
                    ? new Intl.DateTimeFormat('en-IN', {
                        day: '2-digit',
                        month: 'short'
                      }).format(new Date(row.paid_date))
                    : null;

                  return (
                    <div key={row.id} className="flex items-center justify-between px-4 py-3">
                      <div className="text-base">
                        <div className="font-semibold">
                          Day {row.installment_no} • {formatISTDateDisplay(row.due_date)}
                        </div>
                        <div className={statusColor + ' text-sm'}>
                          {row.status === 'paid'
                            ? `Paid ₹${row.paid_amount.toLocaleString('en-IN')}` +
                              (paidDateLabel ? ` on ${paidDateLabel}` : '')
                            : row.status === 'partial'
                            ? `Partial: ₹${row.paid_amount.toLocaleString(
                                'en-IN'
                              )} of ₹${row.due_amount.toLocaleString('en-IN')}`
                            : `Pending: ₹${row.due_amount.toLocaleString('en-IN')}`}
                        </div>
                      </div>
                      <div className="text-right text-lg font-bold">
                        ₹{row.balance_amount.toLocaleString('en-IN')}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Payments List */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-[#E6E6E6] mb-3">Payments</h2>
          <div className="bg-black border border-[#333] rounded-xl overflow-hidden">
            {ledgerRows.length === 0 ? (
              <div className="p-6 text-center text-[#E6E6E6]/70">No payments found.</div>
            ) : (
              <div className="divide-y divide-[#222] max-h-[60vh] overflow-y-auto">
                {ledgerRows.map((r) => (
                  <div key={`${r.id}-${r.created_at}`} className="flex items-start justify-between px-4 py-3">
                    {/* Left: Date + Time */}
                    <div className="text-lg">
                      <div className="font-semibold">{formatISTDateDisplay(r.date)}</div>
                      <div className="text-[#AAAAAA] text-base">{formatISTTime(r.created_at)}</div>
                      {r.remarks ? (
                        <div className="text-[#BBBBBB] text-sm mt-1">{r.remarks}</div>
                      ) : null}
                    </div>
                    {/* Middle: Mode */}
                    <div>
                      <span className="px-3 py-1 border-2 border-[#888] rounded-full text-sm">
                        {r.mode}
                      </span>
                    </div>
                    {/* Right: Amount */}
                    <div className="text-xl font-bold">₹{Number(r.amount).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ledger Page (all accounts for a person)
  if (currentPage === 'ledger' && selectedPersonName) {
    const totalPrincipal = personLedger.reduce((s, r) => s + (r.principal_amount || 0), 0);
    const totalEmi = personLedger.reduce((s, r) => s + (r.emi_amount || 0), 0);
    const totalCollected = personLedger.reduce((s, r) => s + (r.total_collected || 0), 0);
    const totalOutstanding = personLedger.reduce((s, r) => s + (r.outstanding_due || 0), 0);

    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">{selectedPersonName === 'All' ? 'Central Ledger' : selectedPersonName}</h1>
        </div>

        {/* Totals (hidden on Central Ledger All) */}
        {selectedPersonName !== 'All' && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total Principal</span>
              </div>
              <p className="text-2xl font-bold">₹{totalPrincipal.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total EMI</span>
              </div>
              <p className="text-2xl font-bold">₹{totalEmi.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Total Collected</span>
              </div>
              <p className="text-2xl font-bold">₹{totalCollected.toFixed(2)}</p>
            </div>
            <div className="bg-black border border-[#333] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={20} className="text-white" />
                <span className="text-sm font-bold">Outstanding Due</span>
              </div>
              <p className="text-2xl font-bold">₹{totalOutstanding.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Accounts List (hidden on Central Ledger All) */}
        {selectedPersonName !== 'All' && (
          <div className="px-4 pb-6">
            <h2 className="text-xl font-bold text-white mb-3">Accounts</h2>
            {personLedger.length === 0 ? (
              <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
                <p className="text-white/70 text-base">No accounts found for {selectedPersonName}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {personLedger.map((acc) => (
                  <div key={acc.id} className="bg-black border border-[#333] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-base">{acc.account_name}</p>
                        {acc.phone && (
                          <p className="text-white/70 text-sm">{acc.phone}</p>
                        )}
                        {acc.account_no && (
                          <p className="text-white/70 text-sm">A/C: {acc.account_no}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Principal</p>
                        <p className="font-semibold">₹{(acc.principal_amount || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">EMI</p>
                        <p className="font-semibold">₹{(acc.emi_amount || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Collected</p>
                        <p className="font-semibold">₹{(acc.total_collected || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black border border-[#333] rounded-lg p-3">
                        <p className="text-white/70">Outstanding</p>
                        <p className="font-semibold">₹{(acc.outstanding_due || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ledger Rows */}
        <div className="px-4 pb-10">
          <h2 className="text-xl font-bold text-white mb-3">{selectedPersonName === 'All' ? 'Central Ledger' : 'Ledger Rows'}</h2>
          {selectedPersonName === 'All' && centralAccounts.length > 0 && (
            <div className="overflow-x-auto mb-6">
              <table className="w-full">
                <thead>
                  <tr className="text-white/70 border-b border-[#333]">
                    <th className="text-left py-2 text-base">CIF</th>
                    <th className="text-left py-2 text-base">Account No</th>
                    <th className="text-left py-2 text-base">Dt. Lent</th>
                    <th className="text-right py-2 text-base">Fresh</th>
                    <th className="text-right py-2 text-base">No. of days</th>
                    <th className="text-right py-2 text-base">EMI</th>
                    <th className="text-right py-2 text-base">% Held</th>
                    <th className="text-right py-2 text-base">Amt. Held</th>
                    <th className="text-left py-2 text-base">As on Dt.</th>
                    <th className="text-left py-2 text-base">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {centralAccounts.map((acc) => {
                    const meta = centralAccountMeta[acc.id] || {};
                    const lent = (meta.loan_date ? new Date(meta.loan_date).toLocaleDateString('en-IN') : '-') as string;
                    const term = (meta.term_days ?? 0) as number;
                    const emi = (acc.emi_amount ?? 0) as number;
                    const fresh = (acc.principal_amount ?? 0) as number;
                    const heldPct = fresh ? `${( (meta.amount_held ?? 0) / fresh * 100 ).toFixed(2)}%` : '0.00%';
                    const heldAmt = (meta.amount_held ?? 0) as number;
                    return (
                      <tr key={acc.id} className="text-white border-b border-[#333]">
                        <td className="py-2 text-base">{acc.account_name}</td>
                        <td className="py-2 text-base">{acc.account_no || '-'}</td>
                        <td className="py-2 text-base">{lent}</td>
                        <td className="py-2 text-base text-right">{fresh.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base text-right">{term}</td>
                        <td className="py-2 text-base text-right">{emi.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base text-right">{heldPct}</td>
                        <td className="py-2 text-base text-right">{heldAmt.toLocaleString('en-IN')}</td>
                        <td className="py-2 text-base">{new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</td>
                        <td className="py-2 text-base">{(meta.status || '').toUpperCase() || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {personLedgerRows.length === 0 ? (
            <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
              <p className="text-white/70 text-base">No ledger rows yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-white/70 border-b border-[#333]">
                    <th className="text-left py-2 text-base">Date</th>
                    <th className="text-left py-2 text-base">Account</th>
                    <th className="text-left py-2 text-base">Mode</th>
                    <th className="text-right py-2 text-base">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {personLedgerRows.map((r, idx) => (
                    <tr key={idx} className="text-white border-b border-[#333]">
                      <td className="py-2 text-base">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                      <td className="py-2 text-base">{r.account_name}</td>
                      <td className="py-2 text-base">{r.mode}</td>
                      <td className="py-2 font-semibold text-base text-right">₹{Number(r.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default App;
